import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/app/types";
import { completeModule as completeModuleApi } from "@/api/completeModule/completeModule";
import { resetModule as resetModuleApi } from "@/api/resetModule/resetModule";
import type { CompleteModuleResponse } from "@/api/completeModule/types";
import type { ResetModuleResponse } from "@/api/resetModule/types";

// CourseModule type definition (from data-model.md)
export interface CourseModule {
  id: number;
  title: string;
  description: string;
  orderNumber: number;
  contentBlocks: ContentBlock[];
}

export interface ContentBlock {
  id: number;
  type: "text" | "video" | "quiz" | "file";
  content: string;
  orderNumber: number;
  metadata: Record<string, unknown> | null;
}

// State interface
export interface ModuleProgressState {
  currentModuleId: number | null;
  currentModule: CourseModule | null;
  completionInProgress: boolean;
  showCelebration: boolean;
  nextModuleId: number | null;
  previousModuleId: number | null;
  error: string | null;
}

// Initial state
const initialState: ModuleProgressState = {
  currentModuleId: null,
  currentModule: null,
  completionInProgress: false,
  showCelebration: false,
  nextModuleId: null,
  previousModuleId: null,
  error: null,
};

// Async thunks with optimistic update pattern
export const completeModule = createAsyncThunk<
  CompleteModuleResponse,
  { moduleId: number },
  { rejectValue: string }
>("moduleProgress/complete", async ({ moduleId }, { rejectWithValue }) => {
  try {
    const response = await completeModuleApi({ id: moduleId });
    return response;
  } catch (error: unknown) {
    const message =
      error instanceof Error &&
      "response" in error &&
      typeof error.response === "object" &&
      error.response !== null &&
      "data" in error.response &&
      typeof error.response.data === "object" &&
      error.response.data !== null &&
      "message" in error.response.data &&
      typeof error.response.data.message === "string"
        ? error.response.data.message
        : "Failed to complete module";
    return rejectWithValue(message);
  }
});

export const resetModule = createAsyncThunk<
  ResetModuleResponse,
  { moduleId: number },
  { rejectValue: string }
>("moduleProgress/reset", async ({ moduleId }, { rejectWithValue }) => {
  try {
    const response = await resetModuleApi({ moduleId });
    return response;
  } catch (error: unknown) {
    const message =
      error instanceof Error &&
      "response" in error &&
      typeof error.response === "object" &&
      error.response !== null &&
      "data" in error.response &&
      typeof error.response.data === "object" &&
      error.response.data !== null &&
      "message" in error.response.data &&
      typeof error.response.data.message === "string"
        ? error.response.data.message
        : "Failed to reset module";
    return rejectWithValue(message);
  }
});

// Slice
const moduleProgressSlice = createSlice({
  name: "moduleProgress",
  initialState,
  reducers: {
    setCurrentModule: (state, action: PayloadAction<CourseModule | null>) => {
      state.currentModule = action.payload;
      state.currentModuleId = action.payload?.id || null;
    },
    setNavigationModules: (
      state,
      action: PayloadAction<{ next: number | null; previous: number | null }>,
    ) => {
      state.nextModuleId = action.payload.next;
      state.previousModuleId = action.payload.previous;
    },
    markCompleteOptimistic: (state) => {
      // Optimistic update - immediate UI feedback
      state.completionInProgress = true;
      state.error = null;
    },
    rollbackComplete: (state) => {
      // Rollback optimistic update on error
      state.completionInProgress = false;
    },
    triggerCelebration: (state) => {
      state.showCelebration = true;
    },
    dismissCelebration: (state) => {
      state.showCelebration = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetModuleProgressState: () => initialState,
  },
  extraReducers: (builder) => {
    // Complete module
    builder
      .addCase(completeModule.pending, (state) => {
        state.completionInProgress = true;
        state.error = null;
      })
      .addCase(completeModule.fulfilled, (state, action) => {
        state.completionInProgress = false;
        // Server data has been synced - check if course is completed
        if (action.payload.data.enrollment.status === "completed") {
          state.showCelebration = true;
        }
      })
      .addCase(completeModule.rejected, (state, action) => {
        state.completionInProgress = false;
        state.error = action.payload || "Failed to complete module";
      });

    // Reset module
    builder
      .addCase(resetModule.pending, (state) => {
        state.completionInProgress = true;
        state.error = null;
      })
      .addCase(resetModule.fulfilled, (state) => {
        state.completionInProgress = false;
        // Dismiss celebration if it was showing
        state.showCelebration = false;
      })
      .addCase(resetModule.rejected, (state, action) => {
        state.completionInProgress = false;
        state.error = action.payload || "Failed to reset module";
      });
  },
});

// Actions
export const {
  setCurrentModule,
  setNavigationModules,
  markCompleteOptimistic,
  rollbackComplete,
  triggerCelebration,
  dismissCelebration,
  clearError,
  resetModuleProgressState,
} = moduleProgressSlice.actions;

// Selectors
export const selectCurrentModule = (state: RootState) =>
  state.moduleProgress?.currentModule;
export const selectCurrentModuleId = (state: RootState) =>
  state.moduleProgress?.currentModuleId;
export const selectCompletionInProgress = (state: RootState) =>
  state.moduleProgress?.completionInProgress;
export const selectShowCelebration = (state: RootState) =>
  state.moduleProgress?.showCelebration;
export const selectNextModuleId = (state: RootState) =>
  state.moduleProgress?.nextModuleId;
export const selectPreviousModuleId = (state: RootState) =>
  state.moduleProgress?.previousModuleId;
export const selectModuleProgressError = (state: RootState) =>
  state.moduleProgress?.error;

// Reducer
export default moduleProgressSlice.reducer;
